/**
 * Joyee Bundle Builder v2.2
 * ─────────────────────────
 * + Product preview drawer (image gallery + info, no variant picker)
 * + Submit button: always static label
 * + Milestone positioning fix (last node offset)
 */
(function () {
  'use strict';

  /* ── Helpers ──────────────────────────────────────────────── */

  function qs(root, sel) { return root ? root.querySelector(sel) : null; }
  function qsa(root, sel) { return root ? [].slice.call(root.querySelectorAll(sel)) : []; }
  function safeJSON(text, fb) { try { return JSON.parse(text); } catch (_) { return fb; } }

  function formatMoney(cents, fmt) {
    var v = Number(cents || 0);
    var a = (v / 100).toFixed(2);
    var n = String(Math.round(v / 100));
    var c = a.replace('.', ',');
    var f = fmt || '${{amount}}';
    return f
      .replace(/\{\{\s*amount\s*\}\}/g, a)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, n)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, c);
  }

  function esc(t) {
    return String(t == null ? '' : t)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function uid() { return 'jb_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8); }
  function imgSrc(img) { if (!img) return ''; if (typeof img === 'string') return img; return img.src || ''; }
  function variantLabel(v) { if (!v) return ''; return v.public_title || v.title || ''; }

  /* ── localStorage ────────────────────────────────────────── */

  function draftKey(k) { return 'joyee_bundle:' + k; }
  function readDraft(k) { return safeJSON(localStorage.getItem(draftKey(k)) || '[]', []); }
  function writeDraft(k, items) { localStorage.setItem(draftKey(k), JSON.stringify(items)); }
  function clearDraft(k) { localStorage.removeItem(draftKey(k)); }

  /* ── Discount ────────────────────────────────────────────── */

  function pctToRate(v) { var n = Number(v || 0); return n > 1 ? n / 100 : n; }
  function tierRate(tier) { return pctToRate(tier.discount_percent); }
  function tierPctLabel(tier) { var r = tierRate(tier); return r > 0 ? Math.round(r * 100) + '%' : ''; }

  /* ── State ───────────────────────────────────────────────── */

  function calcState(items, tiers) {
    var count = 0, subtotal = 0;
    items.forEach(function (it) { var q = Number(it.quantity || 1); count += q; subtotal += Number(it.price || 0) * q; });

    var unlocked = null;
    tiers.forEach(function (t) {
      if ((Number(t.quantity || 0) > 0 && count >= Number(t.quantity)) || (Number(t.amount || 0) > 0 && subtotal >= Number(t.amount)))
        unlocked = t;
    });

    var nextTier = null;
    for (var i = 0; i < tiers.length; i++) {
      var t = tiers[i];
      var met = (Number(t.quantity || 0) > 0 && count >= Number(t.quantity)) || (Number(t.amount || 0) > 0 && subtotal >= Number(t.amount));
      if (!met) { nextTier = t; break; }
    }

    var rate = unlocked ? tierRate(unlocked) : 0;
    var discSub = rate > 0 ? Math.round(subtotal * (1 - rate)) : subtotal;
    return { count: count, subtotal: subtotal, unlocked: unlocked, nextTier: nextTier, rate: rate, discountedSubtotal: discSub, savings: subtotal - discSub };
  }

  function statusText(s, mf, empty) {
    if (!s.count) return empty || 'Start adding products to build your bundle.';
    var parts = [];
    if (s.rate > 0 && s.unlocked) parts.push('<strong>' + Math.round(s.rate * 100) + '% off applied!</strong>');
    if (s.nextTier) {
      var qR = Number(s.nextTier.quantity || 0) > 0 ? Math.max(Number(s.nextTier.quantity) - s.count, 0) : 0;
      var aR = Number(s.nextTier.amount || 0) > 0 ? Math.max(Number(s.nextTier.amount) - s.subtotal, 0) : 0;
      var nL = tierPctLabel(s.nextTier); nL = nL ? nL + ' off' : 'the next tier';
      if (qR > 0 && aR > 0) parts.push('Add ' + qR + ' more or spend ' + formatMoney(aR, mf) + ' more to unlock ' + nL + '.');
      else if (qR > 0) parts.push('Add ' + qR + ' more to unlock ' + nL + '.');
      else if (aR > 0) parts.push('Spend ' + formatMoney(aR, mf) + ' more to unlock ' + nL + '.');
    } else if (s.unlocked && !parts.length) {
      parts.push(s.unlocked.label || 'Best bundle savings unlocked!');
    }
    return parts.join(' ');
  }

  function progressInfo(s, tiers) {
    if (!tiers.length) return { percent: 0, milestones: [] };
    var total = tiers.length, filled = 0, partial = 0;
    for (var i = 0; i < tiers.length; i++) {
      var t = tiers[i];
      var met = (Number(t.quantity || 0) > 0 && s.count >= Number(t.quantity)) || (Number(t.amount || 0) > 0 && s.subtotal >= Number(t.amount));
      if (met) { filled++; } else {
        var tQ = Number(t.quantity || 0), tA = Number(t.amount || 0);
        if (tQ > 0) partial = Math.min(s.count / tQ, 1);
        else if (tA > 0) partial = Math.min(s.subtotal / tA, 1);
        break;
      }
    }
    var pct = ((filled + partial) / total) * 100;
    var ms = tiers.map(function (t, idx) {
      return { position: ((idx + 1) / total) * 100, label: tierPctLabel(t) || (Number(t.quantity || 0) > 0 ? t.quantity + '' : ''), met: idx < filled };
    });
    return { percent: Math.min(pct, 100), milestones: ms };
  }

  /* ── Constructor ─────────────────────────────────────────── */

  function BB(el) {
    var d = el.dataset;
    this.el = el;
    this.bundleKey = d.bundleKey || 'bundle';
    this.moneyFmt = d.moneyFormat || '${{amount}}';
    this.cartUrl = d.cartUrl || '/cart';
    this.cartAddUrl = d.cartAddUrl || '/cart/add.js';
    this.emptyText = d.emptyText || '';
    this.addLabel = d.addLabel || 'Add bundle to cart';
    this.itemsShortLabel = d.itemsShortLabel || 'items';
    this.batchSize = Math.max(Number(d.revealBatch || 8), 1);
    this.minItems = Number(d.bundleMin || 0);
    this.maxItems = Number(d.bundleMax || 0);
    this.columnsClass = d.columnsClass || 'small-6 medium-4 large-4';
    this.addBundleLabel = d.addBundleLabel || 'Add to bundle';
    this._fetchedPanels = {};  // track which panels have been fetched
    this._fetchingPanel = null; // currently fetching panel handle

    this.tiers = safeJSON((qs(el, '[data-bundle-tiers]') || {}).textContent || '[]', [])
      .filter(function (t) { return Number(t.quantity || 0) > 0 || Number(t.amount || 0) > 0; });
    this.items = readDraft(this.bundleKey);

    // DOM refs
    this.tabs = qsa(el, '[data-bundle-tab]');
    this.tabsContainer = qs(el, '[data-bundle-tabs]');
    this.tabsTrack = qs(el, '[data-bundle-tabs-track]');
    this.tabsThumb = qs(el, '[data-bundle-tabs-thumb]');
    this.panels = qsa(el, '[data-bundle-panel]');
    this.summary = qs(el, '[data-bundle-summary]');
    this.countEl = qs(el, '[data-bundle-count]');
    this.subtotalEl = qs(el, '[data-bundle-subtotal]');
    this.statusEl = qs(el, '[data-bundle-status]');
    this.progressBar = qs(el, '[data-bundle-progress-bar]');
    this.progressMilestones = qs(el, '[data-bundle-milestones]');
    this.itemsEl = qs(el, '[data-bundle-items]');
    this.submitBtn = qs(el, '[data-bundle-submit]');

    this.mobileSubtotal = qs(el, '[data-bundle-mobile-subtotal]');
    this.mobileBadge = qs(el, '[data-bundle-mobile-badge]');
    this.mobileProgressBar = qs(el, '[data-bundle-mobile-progress-bar]');
    this.mobileProgressMilestones = qs(el, '[data-bundle-mobile-milestones]');
    this.mobileHelper = qs(el, '[data-bundle-mobile-helper]');
    this.mobileSheet = qs(el, '[data-bundle-mobile-sheet]');
    this.mobileSheetTarget = qs(el, '[data-bundle-mobile-summary-target]');
    this.mobileBarLabel = qs(el, '[data-bundle-mobile-bar-label]');

    // Variant drawer
    this.drawerRoot = qs(el, '[data-bundle-variant-drawer]');
    this.drawerOverlay = qs(el, '[data-bundle-variant-overlay]');
    this.drawerList = qs(el, '[data-bundle-variant-list]');
    this.drawerTitle = qs(el, '[data-bundle-variant-title]');
    this.drawerPrice = qs(el, '[data-bundle-variant-price]');
    this.drawerImage = qs(el, '[data-bundle-variant-image]');
    this.drawerSubmit = qs(el, '[data-bundle-variant-submit]');
    this.drawerCloseButtons = qsa(el, '[data-bundle-variant-close]');
    this.drawerHero = qs(el, '[data-bundle-variant-hero]');
    this.drawerHeroImg = qs(el, '[data-bundle-variant-hero-img]');
    this.drawerHeroPrev = qs(el, '[data-bundle-hero-prev]');
    this.drawerHeroNext = qs(el, '[data-bundle-hero-next]');
    this.drawerViewLink = qs(el, '[data-bundle-variant-link]');
    this.drawerImages = [];
    this.drawerHeroIndex = 0;
    this.drawerSelected = {};
    this.PILL_THRESHOLD = 6;

    // Preview drawer
    this.previewRoot = qs(el, '[data-bundle-preview-drawer]');
    this.previewOverlay = qs(el, '[data-bundle-preview-overlay]');
    this.previewBody = qs(el, '[data-bundle-preview-body]');
    this.previewCloseButtons = qsa(el, '[data-bundle-preview-close]');

    this.currentProduct = null;
    this.selectedVariant = null;
    this.activeTab = this.tabs.length ? this.tabs[0].getAttribute('data-bundle-tab') : 'all';
    this._loading = false;
    this._sheetTimer = null;

    this._moveToBody(this.drawerOverlay);
    this._moveToBody(this.drawerRoot);
    this._moveToBody(this.previewOverlay);
    this._moveToBody(this.previewRoot);
    this._bind();
    this._setupPanels();
    this._renderMilestoneMarkers();
    this._initTabScroll();
    this.render();
    this.switchTab(this.activeTab);

  }

  BB.prototype._moveToBody = function (el) {
    if (el && el.parentNode !== document.body) document.body.appendChild(el);
  };

  /* ── Milestone markers ───────────────────────────────────── */

  BB.prototype._renderMilestoneMarkers = function () {
    if (!this.tiers.length) return;
    var total = this.tiers.length;
    var html = '';
    this.tiers.forEach(function (t, i) {
      var pos = ((i + 1) / total) * 100;
      // Pull last milestone slightly left so label doesn't overflow
      var isLast = i === total - 1;
      var style = isLast ? 'left:calc(' + pos + '% - 6px)' : 'left:' + pos + '%';
      var label = tierPctLabel(t);
      if (!label) label = Number(t.quantity || 0) > 0 ? t.quantity + ' items' : '';
      html += '<div class="jbb__milestone" style="' + style + '">'
            + '<span class="jbb__milestone-dot" data-milestone-idx="' + i + '"></span>'
            + '<span class="jbb__milestone-label">' + esc(label) + '</span>'
            + '</div>';
    });
    if (this.progressMilestones) this.progressMilestones.innerHTML = html;
    if (this.mobileProgressMilestones) this.mobileProgressMilestones.innerHTML = html;
  };

  /* ── Tab scroll indicator ──────────────────────────────────── */

  BB.prototype._initTabScroll = function () {
    var self = this;
    if (!this.tabsContainer || !this.tabsTrack || !this.tabsThumb) return;

    this.tabsContainer.addEventListener('scroll', function () { self._updateTabScroll(); }, { passive: true });
    window.addEventListener('resize', function () { self._updateTabScroll(); });

    // Initial check after a brief delay (fonts may shift widths)
    setTimeout(function () { self._updateTabScroll(); }, 100);
  };

  BB.prototype._updateTabScroll = function () {
    var el = this.tabsContainer;
    if (!el || !this.tabsTrack || !this.tabsThumb) return;

    var scrollW = el.scrollWidth;
    var clientW = el.clientWidth;

    // If no overflow, hide the track
    if (scrollW <= clientW + 2) {
      this.tabsTrack.hidden = true;
      return;
    }

    this.tabsTrack.hidden = false;

    // Thumb width = proportion of visible area
    var ratio = clientW / scrollW;
    var thumbPct = Math.max(ratio * 100, 15); // at least 15% so it's visible

    // Thumb position
    var maxScroll = scrollW - clientW;
    var scrollPct = maxScroll > 0 ? (el.scrollLeft / maxScroll) : 0;
    var leftPct = scrollPct * (100 - thumbPct);

    this.tabsThumb.style.width = thumbPct + '%';
    this.tabsThumb.style.left = leftPct + '%';
  };

  /* ── Event binding ───────────────────────────────────────── */

  BB.prototype._bind = function () {
    var self = this;

    this.tabs.forEach(function (tab) {
      tab.addEventListener('click', function () { self.switchTab(tab.getAttribute('data-bundle-tab')); });
    });

    this.el.addEventListener('click', function (e) {
      // Product preview (image/title click)
      var preview = e.target.closest('[data-bundle-preview]');
      if (preview) {
        e.preventDefault();
        self.openPreview(preview.dataset.productHandle, preview.dataset.productUrl);
        return;
      }

      // Add to bundle trigger
      var trigger = e.target.closest('[data-bundle-trigger]');
      if (trigger) {
        e.preventDefault();
        if (trigger.dataset.singleVariant === 'true') {
          var card = trigger.closest('.jbb__card');
          if (card) { self._addSingleVariant(card); return; }
        }
        self.openDrawer(trigger.dataset.productHandle, trigger.dataset.productSource, trigger.dataset.productUrl);
        return;
      }

      var removeBtn = e.target.closest('[data-bundle-remove]');
      if (removeBtn) { e.preventDefault(); self.removeItem(removeBtn.getAttribute('data-bundle-remove')); return; }
      var plusBtn = e.target.closest('[data-bundle-qty-plus]');
      if (plusBtn) { e.preventDefault(); self.changeQty(plusBtn.getAttribute('data-bundle-qty-plus'), 1); return; }
      var minusBtn = e.target.closest('[data-bundle-qty-minus]');
      if (minusBtn) { e.preventDefault(); self.changeQty(minusBtn.getAttribute('data-bundle-qty-minus'), -1); return; }
      if (e.target.closest('[data-bundle-submit]')) { e.preventDefault(); self.submit(); return; }
      if (e.target.closest('[data-bundle-clear]')) { e.preventDefault(); self.items = []; clearDraft(self.bundleKey); self.render(); return; }
      if (e.target.closest('[data-bundle-mobile-toggle]')) { e.preventDefault(); self.openSheet(); return; }
      if (e.target.closest('[data-bundle-mobile-close]')) { e.preventDefault(); self.closeSheet(); return; }
    });

    // Variant drawer
    if (this.drawerOverlay) this.drawerOverlay.addEventListener('click', function () { self.closeDrawer(); });
    this.drawerCloseButtons.forEach(function (b) { b.addEventListener('click', function () { self.closeDrawer(); }); });
    // Submit: use event delegation on drawerRoot for robustness
    if (this.drawerRoot) this.drawerRoot.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-bundle-variant-submit]');
      if (btn && !btn.disabled) { e.preventDefault(); self._addFromDrawer(); }
      var prev = e.target.closest('[data-bundle-hero-prev]');
      if (prev) { self._setHeroImage(self.drawerHeroIndex - 1); }
      var next = e.target.closest('[data-bundle-hero-next]');
      if (next) { self._setHeroImage(self.drawerHeroIndex + 1); }
    });

    // Preview drawer
    if (this.previewOverlay) this.previewOverlay.addEventListener('click', function () { self.closePreview(); });
    this.previewCloseButtons.forEach(function (b) { b.addEventListener('click', function () { self.closePreview(); }); });

    var onScroll = function () { self._lazyReveal(); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { self.closeDrawer(); self.closePreview(); self.closeSheet(); }
    });
  };

  /* ── Product preview drawer ──────────────────────────────── */

  BB.prototype.openPreview = function (handle, productUrl) {
    var self = this;
    var url = (productUrl || '/products/' + handle) + '.js';

    if (this.previewBody) this.previewBody.innerHTML = '<div class="jbb-preview__loading"></div>';
    if (this.previewRoot) this.previewRoot.classList.add('active');
    if (this.previewOverlay) this.previewOverlay.classList.add('active');
    document.body.style.overflow = 'hidden';

    fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(function (product) { self._renderPreview(product); })
      .catch(function () {
        if (self.previewBody) self.previewBody.innerHTML = '<div class="jbb-preview__error">Unable to load product.</div>';
      });
  };

  BB.prototype._renderPreview = function (product) {
    if (!this.previewBody) return;
    var mf = this.moneyFmt;
    var images = product.images || [];
    if (!images.length && product.featured_image) images = [imgSrc(product.featured_image)];

    var html = '';

    // Images
    html += '<div class="jbb-preview__images">';
    images.forEach(function (img) {
      var src = typeof img === 'string' ? img : (img.src || '');
      if (src) html += '<img class="jbb-preview__img" src="' + esc(src) + '" alt="" loading="lazy">';
    });
    html += '</div>';

    // Info
    html += '<div class="jbb-preview__info">';
    html += '<h3 class="jbb-preview__title">' + esc(product.title || '') + '</h3>';
    if (product.price) {
      html += '<div class="jbb-preview__price">';
      if (product.compare_at_price && product.compare_at_price > product.price) {
        html += '<s>' + formatMoney(product.compare_at_price, mf) + '</s> ';
      }
      html += '<strong>' + formatMoney(product.price, mf) + '</strong>';
      html += '</div>';
    }
    if (product.description) {
      html += '<div class="jbb-preview__desc rte">' + product.description + '</div>';
    }
    html += '</div>';

    this.previewBody.innerHTML = html;
  };

  BB.prototype.closePreview = function () {
    if (this.previewRoot) this.previewRoot.classList.remove('active');
    if (this.previewOverlay) this.previewOverlay.classList.remove('active');
    // Restore body scroll only if no other overlay is open
    if (!this._isDrawerOpen() && !(this.mobileSheet && !this.mobileSheet.hidden)) {
      document.body.style.overflow = '';
    }
  };

  BB.prototype._isDrawerOpen = function () {
    return this.drawerRoot && this.drawerRoot.classList.contains('active');
  };

  /* ── Panels & lazy-load ──────────────────────────────────── */

  BB.prototype._setupPanels = function () {
    var batch = this.batchSize;
    this.panels.forEach(function (p) {
      var cards = qsa(p, '[data-bundle-idx]');
      p._cards = cards; p._shown = 0;
      cards.forEach(function (c) { c.hidden = true; });
      _revealN(p, batch);
    });
  };

  function _revealN(p, n) {
    if (!p || !p._cards) return;
    var end = Math.min(p._shown + n, p._cards.length);
    for (var i = p._shown; i < end; i++) p._cards[i].hidden = false;
    p._shown = end;
  }

  BB.prototype._lazyReveal = function () {
    var p = this._activePanel();
    if (!p || p.hidden) return;

    // Reveal already-loaded hidden cards
    if (p._cards && p._shown < p._cards.length && !this._loading) {
      if (p.getBoundingClientRect().bottom - (window.innerHeight || document.documentElement.clientHeight) < 320) {
        this._loading = true; _revealN(p, this.batchSize); this._loading = false;
      }
    }

    // Fetch next page if all current cards are shown and there are more pages
    if (p._cards && p._shown >= p._cards.length && !p._lazyDone && !this._fetchingPanel) {
      var handle = p.getAttribute('data-collection-handle');
      if (handle && p.getBoundingClientRect().bottom - (window.innerHeight || document.documentElement.clientHeight) < 600) {
        var nextPage = (p._lazyPage || 1) + 1;
        this._fetchCollection(p, handle, nextPage);
      }
    }
  };

  BB.prototype._activePanel = function () {
    var key = this.activeTab;
    return this.panels.find(function (p) { return p.getAttribute('data-bundle-panel') === key; }) || null;
  };

  /* ── Tab switching ───────────────────────────────────────── */

  BB.prototype.switchTab = function (key) {
    this.activeTab = key;
    var active = null;
    this.tabs.forEach(function (t) { var on = t.getAttribute('data-bundle-tab') === key; t.classList.toggle('is-active', on); if (on) active = t; });
    this.panels.forEach(function (p) { p.hidden = p.getAttribute('data-bundle-panel') !== key; });
    if (active) active.scrollIntoView({ behavior: 'auto', inline: 'center', block: 'nearest' });
    var anchor = this.el.querySelector('.jbb__hero') || this.el.querySelector('[data-bundle-top-anchor]');
    var offset = window.innerWidth <= 1060 ? 64 : 80;
    if (anchor) { var top = Math.max(window.pageYOffset + anchor.getBoundingClientRect().top - offset - 12, 0); window.scrollTo({ top: top, behavior: 'auto' }); }

    // Lazy-fetch products for panels that haven't been loaded yet
    var panel = this._activePanel();
    if (panel && !this._fetchedPanels[key] && panel.getAttribute('data-collection-handle')) {
      var grid = qs(panel, '.products.row');
      if (grid && !grid.children.length) {
        this._fetchCollection(panel, panel.getAttribute('data-collection-handle'), 1);
      } else {
        this._fetchedPanels[key] = true;
      }
    }

    this._lazyReveal();
    var self = this;
    setTimeout(function () { self._updateTabScroll(); }, 50);
  };

  /* ── Lazy-fetch collection products ──────────────────────── */

  BB.prototype._fetchCollection = function (panel, handle, page) {
    var self = this;
    if (this._fetchingPanel === handle) return;
    this._fetchingPanel = handle;

    var loader = qs(panel, '[data-bundle-load-more]');
    if (loader) loader.hidden = false;

    var url = '/collections/' + handle + '/products.json?limit=50&page=' + page;
    fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(function (data) {
        var grid = qs(panel, '.products.row');
        if (!grid) return;

        var startIdx = grid.children.length;
        var products = data.products || [];

        products.forEach(function (p, i) {
          var idx = startIdx + i;
          var mapped = self._mapProduct(p);
          var cardHTML = self._buildCardHTML(mapped, handle, idx);
          var wrapper = document.createElement('div');
          wrapper.className = self.columnsClass + ' columns jbb__col';
          wrapper.setAttribute('data-bundle-idx', idx);
          if (idx >= self.batchSize) wrapper.hidden = true;
          wrapper.innerHTML = cardHTML;
          grid.appendChild(wrapper);
        });

        // Update panel card refs
        panel._cards = qsa(panel, '[data-bundle-idx]');
        panel._shown = 0;
        _revealN(panel, self.batchSize);

        // Track pagination
        if (!panel._lazyPage) panel._lazyPage = 1;
        panel._lazyPage = page;
        panel._lazyDone = products.length < 50;

        self._fetchedPanels[handle] = true;
        if (loader) loader.hidden = true;
        self._fetchingPanel = null;
        self._lazyReveal();
      })
      .catch(function (err) {
        console.error('Bundle fetch error:', err);
        if (loader) loader.hidden = true;
        self._fetchingPanel = null;
      });
  };

  // Map Shopify products.json format to our card format
  BB.prototype._mapProduct = function (p) {
    var firstVariant = (p.variants && p.variants[0]) || {};
    var isSV = p.variants && p.variants.length === 1 && firstVariant.title === 'Default Title';
    var img = '';
    if (p.image && p.image.src) img = p.image.src;
    else if (p.images && p.images.length && p.images[0].src) img = p.images[0].src;

    var prices = (p.variants || []).map(function (v) { return parseFloat(v.price || '0'); });
    var minPrice = prices.length ? Math.min.apply(null, prices) : 0;
    var maxPrice = prices.length ? Math.max.apply(null, prices) : 0;
    var priceVaries = minPrice !== maxPrice;
    var priceCents = Math.round(parseFloat(firstVariant.price || '0') * 100);
    var minCents = Math.round(minPrice * 100);

    return {
      handle: p.handle,
      title: p.title,
      url: '/products/' + p.handle,
      price: priceCents,
      price_varies: priceVaries,
      price_min: minCents,
      featured_image: img,
      is_single_variant: isSV,
      single_variant_id: firstVariant.id,
      single_variant_price: priceCents
    };
  };

  BB.prototype._buildCardHTML = function (p, sourceHandle, idx) {
    var isSV = p.is_single_variant;
    var ratio = '125%';
    // Match theme aspect ratio
    var root = document.documentElement;
    var imgUrl = p.featured_image || '';
    var priceHTML = p.price_varies
      ? formatMoney(p.price_min, this.moneyFmt)
      : formatMoney(p.price, this.moneyFmt);

    var svAttrs = '';
    if (isSV) {
      svAttrs = ' data-single-variant-id="' + p.single_variant_id + '"'
        + ' data-single-variant-price="' + p.single_variant_price + '"'
        + ' data-single-variant-image="' + esc(imgUrl) + '"'
        + ' data-single-variant-title="' + esc(p.title) + '"';
    }

    return '<div class="jbb__card" data-product-handle="' + esc(p.handle) + '" data-product-source="' + esc(sourceHandle) + '"' + svAttrs + '>'
      + '<div class="jbb__card-media" style="--bundle-ratio: ' + ratio + ';">'
        + '<a class="jbb__card-link" href="' + esc(p.url) + '" data-bundle-preview data-product-handle="' + esc(p.handle) + '" data-product-url="' + esc(p.url) + '">'
          + '<div class="jbb__card-img">'
            + (imgUrl ? '<img src="' + esc(imgUrl) + '" alt="' + esc(p.title) + '" loading="lazy" width="600">' : '')
          + '</div>'
        + '</a>'
        + '<button class="jbb__card-add" type="button" aria-label="' + esc(this.addBundleLabel) + '"'
          + ' data-bundle-trigger data-product-handle="' + esc(p.handle) + '" data-product-url="' + esc(p.url) + '" data-product-source="' + esc(sourceHandle) + '"'
          + (isSV ? ' data-single-variant="true"' : '') + '>'
          + '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
            + '<line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line>'
          + '</svg>'
        + '</button>'
      + '</div>'
      + '<div class="jbb__card-info">'
        + '<div class="jbb__card-title"><a href="' + esc(p.url) + '" data-bundle-preview data-product-handle="' + esc(p.handle) + '" data-product-url="' + esc(p.url) + '">' + esc(p.title) + '</a></div>'
        + '<div class="jbb__card-price">' + priceHTML + '</div>'
      + '</div>'
    + '</div>';
  };

  /* ── Single-variant direct add ───────────────────────────── */

  BB.prototype._addSingleVariant = function (card) {
    var ds = card.dataset, vid = Number(ds.singleVariantId), price = Number(ds.singleVariantPrice);
    if (!vid) return;
    var st = calcState(this.items, this.tiers);
    if (this.maxItems > 0 && st.count >= this.maxItems) { this._flashMax(); return; }
    var existing = this.items.find(function (it) { return it.variant_id === vid; });
    if (existing) { existing.quantity = (existing.quantity || 1) + 1; }
    else {
      this.items.push({ key: uid(), variant_id: vid, handle: ds.productHandle, source: ds.productSource || '',
        title: ds.singleVariantTitle || '', variant_title: '', price: price, quantity: 1,
        image: ds.singleVariantImage || '', url: '/products/' + ds.productHandle });
    }
    writeDraft(this.bundleKey, this.items); this.render(); this._pulseCard(card);
  };

  BB.prototype._pulseCard = function (card) { card.classList.add('jbb__card--added'); setTimeout(function () { card.classList.remove('jbb__card--added'); }, 600); };
  BB.prototype._flashMax = function () {
    if (this.statusEl) { this.statusEl.textContent = 'Maximum ' + this.maxItems + ' items reached.'; var el = this.statusEl; el.classList.add('jbb__status--flash'); setTimeout(function () { el.classList.remove('jbb__status--flash'); }, 1200); }
  };

  /* ── Variant drawer ──────────────────────────────────────── */

  BB.prototype._setHeroImage = function (idx) {
    if (!this.drawerImages.length || !this.drawerHeroImg) return;
    this.drawerHeroIndex = ((idx % this.drawerImages.length) + this.drawerImages.length) % this.drawerImages.length;
    var newSrc = this.drawerImages[this.drawerHeroIndex];
    if (this.drawerHeroImg.src !== newSrc) {
      this.drawerHeroImg.style.opacity = '0';
      var img = this.drawerHeroImg;
      img.onload = function () { img.style.opacity = '1'; img.onload = null; };
      img.src = newSrc;
    }
  };

  BB.prototype._findDrawerVariant = function () {
    var p = this.currentProduct && this.currentProduct.product;
    if (!p) return null;
    var sel = this.drawerSelected;
    for (var i = 0; i < p.options.length; i++) { if (!sel[p.options[i].name]) return null; }
    return p.variants.find(function (v) {
      return p.options.every(function (opt) { return v['option' + opt.position] === sel[opt.name]; });
    }) || null;
  };

  BB.prototype._getDrawerAvailable = function (optName) {
    var p = this.currentProduct.product, sel = this.drawerSelected;
    var pos = p.options.find(function (o) { return o.name === optName; }).position;
    var avail = new Set();
    p.variants.forEach(function (v) {
      if (!v.available) return;
      var ok = true;
      p.options.forEach(function (opt) {
        if (opt.name === optName) return;
        if (sel[opt.name] && v['option' + opt.position] !== sel[opt.name]) ok = false;
      });
      if (ok) avail.add(v['option' + pos]);
    });
    return avail;
  };

  BB.prototype._updateDrawerStates = function () {
    var self = this, p = this.currentProduct.product;
    p.options.forEach(function (opt) {
      var avail = self._getDrawerAvailable(opt.name);
      var grp = self.drawerList.querySelector('[data-option="' + opt.name + '"]');
      if (!grp) return;
      qsa(grp, '.vd-pill').forEach(function (pill) {
        pill.classList.toggle('vd-pill--selected', self.drawerSelected[opt.name] === pill.dataset.value);
        pill.classList.toggle('vd-pill--disabled', !avail.has(pill.dataset.value));
      });
      var sel = qs(grp, 'select');
      if (sel) {
        sel.value = self.drawerSelected[opt.name] || '';
        Array.from(sel.options).forEach(function (o) { if (o.value) o.disabled = !avail.has(o.value); });
      }
    });
    var v = this._findDrawerVariant();
    if (v) {
      var src = imgSrc((v.featured_image && v.featured_image.src) || v.featured_image || p.featured_image);
      if (src && this.drawerHeroImg) {
        var newSrc = src;
        if (this.drawerHeroImg.src !== newSrc) {
          this.drawerHeroImg.style.opacity = '0';
          var img = this.drawerHeroImg;
          img.onload = function () { img.style.opacity = '1'; img.onload = null; };
          img.src = newSrc;
        }
      }
      if (src && this.drawerImage) this.drawerImage.src = src;
      if (this.drawerPrice) this.drawerPrice.innerHTML = formatMoney(v.price, this.moneyFmt);
      this.selectedVariant = v;
      if (this.drawerSubmit) this.drawerSubmit.disabled = false;
    } else {
      this.selectedVariant = null;
      if (this.drawerSubmit) this.drawerSubmit.disabled = true;
    }
    // Custom size input visibility
    var customWrap = this.drawerList && this.drawerList.querySelector('.vd-custom-input');
    if (customWrap) {
      var sizeOpt = p.options.find(function (o) { return /size/i.test(o.name); });
      customWrap.style.display = (sizeOpt && /custom/i.test(self.drawerSelected[sizeOpt.name])) ? 'block' : 'none';
    }
  };

  BB.prototype.openDrawer = function (handle, source, productUrl) {
    var self = this, url = (productUrl || '/products/' + handle) + '.js';
    this.currentProduct = { handle: handle, source: source || '', url: productUrl || '/products/' + handle, product: null };
    this.selectedVariant = null;
    this.drawerSelected = {};
    if (this.drawerTitle) this.drawerTitle.textContent = 'Loading…';
    if (this.drawerPrice) this.drawerPrice.innerHTML = '';
    if (this.drawerList) this.drawerList.innerHTML = '<div class="vd-loading"></div>';
    if (this.drawerHero) this.drawerHero.style.display = 'none';
    if (this.drawerImage) { this.drawerImage.src = ''; this.drawerImage.style.display = 'none'; }
    if (this.drawerSubmit) this.drawerSubmit.disabled = true;
    if (this.drawerRoot) this.drawerRoot.classList.add('active');
    if (this.drawerOverlay) this.drawerOverlay.classList.add('active');
    document.body.style.overflow = 'hidden';
    fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(function (p) { self._renderDrawer(p); })
      .catch(function () { if (self.drawerList) self.drawerList.innerHTML = '<p style="padding:20px;text-align:center;color:#999;">Unable to load.</p>'; });
  };

  BB.prototype._renderDrawer = function (product) {
    var self = this;
    this.currentProduct.product = product;
    this.drawerSelected = {};

    // Images
    this.drawerImages = (product.images || []).map(function (img) {
      return (typeof img === 'object') ? (img.src || '') : (img || '');
    });
    var fallback = imgSrc(product.featured_image);
    if (fallback) {
      if (this.drawerHeroImg) { this.drawerHeroImg.src = fallback; }
      if (this.drawerImage) { this.drawerImage.src = fallback; this.drawerImage.style.display = ''; }
      if (this.drawerHero) this.drawerHero.style.display = '';
      this.drawerHeroIndex = 0;
    }
    var hasMulti = this.drawerImages.length > 1;
    if (this.drawerHeroPrev) this.drawerHeroPrev.style.display = hasMulti ? '' : 'none';
    if (this.drawerHeroNext) this.drawerHeroNext.style.display = hasMulti ? '' : 'none';

    if (this.drawerTitle) this.drawerTitle.textContent = product.title || '';
    if (this.drawerPrice) this.drawerPrice.innerHTML = formatMoney(product.price, this.moneyFmt);
    if (this.drawerViewLink) this.drawerViewLink.href = this.currentProduct.url || '#';

    // Build pills/dropdown HTML
    if (!this.drawerList) return;
    var html = '';
    product.options.forEach(function (opt) {
      var seen = new Set(), values = [];
      product.variants.forEach(function (v) {
        var val = v['option' + opt.position];
        if (!seen.has(val)) { seen.add(val); values.push(val); }
      });
      html += '<div class="vd-group" data-option="' + esc(opt.name) + '">';
      html += '<p class="vd-group__label">' + esc(opt.name) + '</p>';
      if (values.length > self.PILL_THRESHOLD) {
        html += '<select class="vd-select">';
        values.forEach(function (val) { html += '<option value="' + esc(val) + '">' + esc(val) + '</option>'; });
        html += '</select>';
      } else {
        html += '<div class="vd-group__pills">';
        values.forEach(function (val) { html += '<button type="button" class="vd-pill" data-value="' + esc(val) + '">' + esc(val) + '</button>'; });
        html += '</div>';
      }
      html += '</div>';
    });

    // Custom size textarea
    html += '<div class="vd-custom-input" style="display:none;">';
    html += '<label class="vd-group__label">Custom Size-Add Note</label>';
    html += '<textarea class="vd-textarea" data-bundle-custom-note rows="3" placeholder="Custom width: 7–17mm (Almond thumbs up to 18mm). e.g. L: 15, 11, 12, 11, 8 / R: 15, 11, 12, 10, 8 — thumb to pinky. List both hands if different."></textarea>';
    html += '</div>';

    // Description
    if (product.description) {
      var desc = product.description.replace(/<[^>]*>/g, '').trim();
      if (desc.length > 200) desc = desc.substring(0, 200) + '…';
      if (desc) html += '<div class="vd-desc">' + desc + '</div>';
    }

    this.drawerList.innerHTML = html;

    // Bind pills
    qsa(this.drawerList, '.vd-pill').forEach(function (pill) {
      pill.addEventListener('click', function () {
        if (pill.classList.contains('vd-pill--disabled')) return;
        var optName = pill.closest('.vd-group').dataset.option;
        var val = pill.dataset.value;
        self.drawerSelected[optName] = (self.drawerSelected[optName] === val) ? undefined : val;
        if (!self.drawerSelected[optName]) delete self.drawerSelected[optName];
        self._updateDrawerStates();
      });
    });
    // Bind dropdowns
    qsa(this.drawerList, '.vd-select').forEach(function (sel) {
      sel.addEventListener('change', function () {
        var optName = sel.closest('.vd-group').dataset.option;
        if (sel.value) { self.drawerSelected[optName] = sel.value; } else { delete self.drawerSelected[optName]; }
        self._updateDrawerStates();
      });
    });

    // Auto-select first available
    var first = product.variants.find(function (v) { return v.available; });
    if (first) {
      product.options.forEach(function (opt) {
        self.drawerSelected[opt.name] = first['option' + opt.position];
      });
    }
    this._updateDrawerStates();

    // Preload images
    this.drawerImages.forEach(function (src) { if (src) { var i = new Image(); i.src = src; } });
  };

  BB.prototype.closeDrawer = function () {
    if (this.drawerRoot) this.drawerRoot.classList.remove('active');
    if (this.drawerOverlay) this.drawerOverlay.classList.remove('active');
    if (!(this.mobileSheet && !this.mobileSheet.hidden)) document.body.style.overflow = '';
  };

  BB.prototype._addFromDrawer = function () {
    if (!this.selectedVariant || !this.currentProduct || !this.currentProduct.product) return;
    var st = calcState(this.items, this.tiers);
    if (this.maxItems > 0 && st.count >= this.maxItems) { this._flashMax(); this.closeDrawer(); return; }
    var v = this.selectedVariant, p = this.currentProduct.product;
    var vImg = imgSrc((v.featured_image && v.featured_image.src) || v.featured_image || p.featured_image);
    var noteEl = this.drawerList && this.drawerList.querySelector('[data-bundle-custom-note]');
    var customNote = (noteEl && noteEl.value) ? noteEl.value.trim() : '';
    var existing = this.items.find(function (it) { return it.variant_id === Number(v.id) && (it.custom_note || '') === customNote; });
    if (existing) { existing.quantity = (existing.quantity || 1) + 1; }
    else {
      var item = { key: uid(), variant_id: Number(v.id), handle: this.currentProduct.handle,
        source: this.currentProduct.source, title: p.title, variant_title: variantLabel(v),
        price: Number(v.price), quantity: 1, image: vImg,
        url: this.currentProduct.url || '/products/' + this.currentProduct.handle };
      if (customNote) item.custom_note = customNote;
      this.items.push(item);
    }
    writeDraft(this.bundleKey, this.items); this.render(); this.closeDrawer();
  };

  /* ── Item operations ─────────────────────────────────────── */

  BB.prototype.removeItem = function (key) { this.items = this.items.filter(function (it) { return it.key !== key; }); writeDraft(this.bundleKey, this.items); this.render(); };

  BB.prototype.changeQty = function (key, delta) {
    var item = this.items.find(function (it) { return it.key === key; });
    if (!item) return;
    var st = calcState(this.items, this.tiers), newQty = (item.quantity || 1) + delta;
    if (newQty <= 0) { this.removeItem(key); return; }
    if (delta > 0 && this.maxItems > 0 && st.count >= this.maxItems) { this._flashMax(); return; }
    item.quantity = newQty; writeDraft(this.bundleKey, this.items); this.render();
  };

  /* ── Rendering ───────────────────────────────────────────── */

  BB.prototype._subtotalHTML = function (s) {
    if (s.rate > 0) return '<span class="jbb__price-compare"><s>' + formatMoney(s.subtotal, this.moneyFmt) + '</s> <strong>' + formatMoney(s.discountedSubtotal, this.moneyFmt) + '</strong></span>';
    return '<strong>' + formatMoney(s.subtotal, this.moneyFmt) + '</strong>';
  };

  BB.prototype._itemPriceHTML = function (item, s) {
    var lt = Number(item.price || 0) * Number(item.quantity || 1);
    if (s.rate > 0) { var d = Math.round(lt * (1 - s.rate)); return '<span class="jbb__price-compare"><s>' + formatMoney(lt, this.moneyFmt) + '</s> <strong>' + formatMoney(d, this.moneyFmt) + '</strong></span>'; }
    return formatMoney(lt, this.moneyFmt);
  };

  BB.prototype.render = function () {
    var state = calcState(this.items, this.tiers), self = this;

    if (this.countEl) this.countEl.textContent = state.count;
    if (this.subtotalEl) this.subtotalEl.innerHTML = this._subtotalHTML(state);
    if (this.statusEl) this.statusEl.innerHTML = statusText(state, this.moneyFmt, this.emptyText);

    var prog = progressInfo(state, this.tiers);
    if (this.progressBar) this.progressBar.style.width = prog.percent + '%';
    if (this.mobileProgressBar) this.mobileProgressBar.style.width = prog.percent + '%';
    this._updateMilestoneDots(this.progressMilestones, prog);
    this._updateMilestoneDots(this.mobileProgressMilestones, prog);

    // Mobile bar label
    if (this.mobileBarLabel) {
      this.mobileBarLabel.textContent = state.count > 0 ? 'View bundle \u00B7 ' + state.count + ' ' + this.itemsShortLabel : 'Your bundle';
    }
    if (this.mobileSubtotal) this.mobileSubtotal.innerHTML = state.rate > 0 ? formatMoney(state.discountedSubtotal, this.moneyFmt) : formatMoney(state.subtotal, this.moneyFmt);
    if (this.mobileBadge) { var bt = state.rate > 0 ? Math.round(state.rate * 100) + '% off' : ''; this.mobileBadge.textContent = bt; this.mobileBadge.hidden = !bt; }
    if (this.mobileHelper) this.mobileHelper.innerHTML = statusText(state, this.moneyFmt, '');

    // Submit button — always static label
    if (this.submitBtn) {
      var canSubmit = this.items.length > 0;
      if (this.minItems > 0 && state.count < this.minItems) canSubmit = false;
      this.submitBtn.disabled = !canSubmit;
      this.submitBtn.textContent = this.addLabel;
    }

    if (!this.itemsEl) return;
    if (!this.items.length) { this.itemsEl.innerHTML = ''; this._syncSheet(); return; }

    this.itemsEl.innerHTML = this.items.map(function (item) {
      var q = item.quantity || 1;
      var vt = (item.variant_title || '').replace(/ \/ /g, ',');
      return '<div class="jbb__item">' +
        '<img class="jbb__item-img" src="' + esc(item.image || '') + '" alt="">' +
        '<div class="jbb__item-body">' +
          '<div class="jbb__item-title">' + esc(item.title) + '</div>' +
          '<div class="jbb__item-price">' + self._itemPriceHTML(item, state) + '</div>' +
          (vt ? '<div class="jbb__item-variant">' + esc(vt) + '</div>' : '') +
          (item.custom_note ? '<div class="jbb__item-variant">Custom Size Note: ' + esc(item.custom_note) + '</div>' : '') +
        '</div>' +
        '<div class="jbb__item-qty">' +
          '<button type="button" class="jbb__qty-btn" data-bundle-qty-minus="' + esc(item.key) + '">−</button>' +
          '<span class="jbb__qty-val">' + q + '</span>' +
          '<button type="button" class="jbb__qty-btn" data-bundle-qty-plus="' + esc(item.key) + '">+</button>' +
        '</div>' +
        '<button type="button" class="jbb__item-remove" data-bundle-remove="' + esc(item.key) + '">' +
          '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
        '</button>' +
      '</div>';
    }).join('');
    this._syncSheet();
  };

  BB.prototype._updateMilestoneDots = function (container, prog) {
    if (!container) return;
    qsa(container, '[data-milestone-idx]').forEach(function (dot) {
      dot.classList.toggle('is-met', prog.milestones[Number(dot.getAttribute('data-milestone-idx'))] && prog.milestones[Number(dot.getAttribute('data-milestone-idx'))].met);
    });
  };

  /* ── Mobile sheet ────────────────────────────────────────── */

  BB.prototype._syncSheet = function () {
    if (!this.mobileSheetTarget || !this.summary || !this.mobileSheet || this.mobileSheet.hidden) return;
    this.mobileSheetTarget.innerHTML = this.summary.outerHTML;
  };
  BB.prototype.openSheet = function () {
    var self = this; if (!this.mobileSheet) return;
    if (this._sheetTimer) { clearTimeout(this._sheetTimer); this._sheetTimer = null; }
    if (this.mobileSheetTarget && this.summary) this.mobileSheetTarget.innerHTML = this.summary.outerHTML;
    this.mobileSheet.hidden = false;
    requestAnimationFrame(function () { self.mobileSheet.classList.add('is-open'); });
    document.body.style.overflow = 'hidden';
  };
  BB.prototype.closeSheet = function () {
    var self = this; if (!this.mobileSheet) return;
    this.mobileSheet.classList.remove('is-open');
    if (this._sheetTimer) clearTimeout(this._sheetTimer);
    this._sheetTimer = setTimeout(function () { self.mobileSheet.hidden = true; self._sheetTimer = null; }, 280);
    document.body.style.overflow = '';
  };

  /* ── Submit ──────────────────────────────────────────────── */

  BB.prototype.submit = function () {
    var self = this; if (!this.items.length) return;
    var state = calcState(this.items, this.tiers);
    if (this.minItems > 0 && state.count < this.minItems) return;

    var map = {};
    this.items.forEach(function (it) {
      var key = it.variant_id + '|' + (it.custom_note || '');
      if (map[key]) { map[key].quantity += (it.quantity || 1); }
      else {
        var entry = { id: it.variant_id, quantity: it.quantity || 1 };
        if (it.custom_note) entry.properties = { 'Custom Size Note': it.custom_note };
        map[key] = entry;
      }
    });
    var payload = { items: Object.keys(map).map(function (k) { return map[k]; }) };
    if (this.submitBtn) this.submitBtn.disabled = true;

    fetch(this.cartAddUrl, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload) })
    .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
    .then(function () {
      self.items = []; clearDraft(self.bundleKey); self.render(); self.closeSheet();
      if (state.unlocked && state.unlocked.code) {
        var cur = window.location.pathname + window.location.search;
        window.location.href = '/discount/' + encodeURIComponent(state.unlocked.code) + '?redirect=' + encodeURIComponent(cur + (cur.indexOf('?') > -1 ? '&' : '?') + '_bundle_done=1');
        return;
      }
      self._refreshAndOpenCart();
    })
    .catch(function (err) { console.error('Bundle submit:', err); alert('Unable to add bundle. Please try again.'); })
    .finally(function () { if (self.submitBtn) self.submitBtn.disabled = !self.items.length; });
  };

  BB.prototype._refreshAndOpenCart = function () {
    var cartDrawer = document.getElementById('Cart-Drawer');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      fetch(window.location.pathname + '?sections=cart-drawer,cart-bubble')
        .then(function (r) { return r.json(); })
        .then(function (json) {
          var el = document.getElementById('Cart-Drawer');
          if (el && json['cart-drawer']) { var d = new DOMParser().parseFromString(json['cart-drawer'], 'text/html').querySelector('.cart-drawer'); if (d && el.querySelector('.cart-drawer')) el.querySelector('.cart-drawer').innerHTML = d.innerHTML; }
          var b = document.getElementById('cart-drawer-toggle');
          if (b && json['cart-bubble']) { var bd = new DOMParser().parseFromString(json['cart-bubble'], 'text/html').querySelector('.thb-item-count'); if (bd) { var t = b.querySelector('.thb-item-count'); if (t) t.innerHTML = bd.innerHTML; } }
          if (el && typeof el.open === 'function') el.open();
        })
        .catch(function () { if (cartDrawer && typeof cartDrawer.open === 'function') cartDrawer.open(); });
      return;
    }
    var toggle = document.getElementById('cart-drawer-toggle');
    if (toggle) { setTimeout(function () { toggle.click(); }, 200); return; }
    window.location.href = this.cartUrl;
  };

  function checkBundleDoneParam() {
    if (window.location.search.indexOf('_bundle_done=1') === -1) return;
    var clean = window.location.href.replace(/[?&]_bundle_done=1/, '').replace(/\?$/, '');
    window.history.replaceState(null, '', clean);
    setTimeout(function () {
      var cd = document.getElementById('Cart-Drawer');
      if (cd && typeof cd.open === 'function') {
        fetch(window.location.pathname + '?sections=cart-drawer,cart-bubble').then(function (r) { return r.json(); }).then(function (json) {
          if (json['cart-drawer']) { var d = new DOMParser().parseFromString(json['cart-drawer'], 'text/html').querySelector('.cart-drawer'); if (d && cd.querySelector('.cart-drawer')) cd.querySelector('.cart-drawer').innerHTML = d.innerHTML; }
          cd.open();
        }).catch(function () { cd.open(); });
      }
    }, 500);
  }

  /* ── Init ────────────────────────────────────────────────── */

  function init(root) { qsa(root || document, '.jbb').forEach(function (el) { if (el._jbb) return; el._jbb = new BB(el); }); }
  document.addEventListener('DOMContentLoaded', function () { init(document); checkBundleDoneParam(); });
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:reorder', function () { init(document); });
})();